import { useEffect, useState } from "react";
import { Apple, Check, Download, Laptop, Monitor, Share as ShareIcon, Smartphone, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// Pré-sélectionne la plateforme détectée (l'utilisateur confirme ou corrige).
const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mac OS X/.test(ua)) return "mac";
  return "pc";
};

// L'app tourne-t-elle déjà en mode installé (standalone) ?
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.matchMedia?.("(display-mode: window-controls-overlay)").matches ||
  navigator.standalone === true;

const DEVICES = [
  { id: "pc", Icon: Monitor },
  { id: "mac", Icon: Laptop },
  { id: "ios", Icon: Apple },
  { id: "android", Icon: Smartphone },
];

export const InstallPrompt = () => {
  const { t } = useAuth();
  const [detected] = useState(detectPlatform);
  const [platform, setPlatform] = useState(detectPlatform);
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("rannea_install_dismissed") === "1");
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    // Chrome/Edge (PC, Mac, Android) exposent l'installation native via cet événement
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;

  // « Plus tard » : masqué pour cette visite uniquement — la demande revient à la
  // prochaine ouverture par URL tant que l'app n'est pas installée (choix utilisateur).
  const later = () => {
    sessionStorage.setItem("rannea_install_dismissed", "1");
    setDismissed(true);
  };

  const installNow = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 p-4" data-testid="install-prompt" role="dialog" aria-modal="true" aria-label={t("install.title")}>
      <div className="app-card w-full max-w-md p-6 space-y-5 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/pwa-192.png" alt="RANNEA" className="h-12 w-12 rounded-xl border border-app" />
            <div>
              <h2 className="font-heading text-lg font-bold">{t("install.title")}</h2>
              <p className="text-xs t-secondary">{t("install.subtitle")}</p>
            </div>
          </div>
          <button onClick={later} className="t-secondary hover:text-white transition-colors shrink-0" data-testid="install-close-button" aria-label={t("install.later")}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Installation directe en 1 clic (Chrome/Edge — PC, Mac, Android) : le navigateur
            expose beforeinstallprompt. iOS/Safari ne le permet pas → guidage manuel ci-dessous. */}
        {deferred && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="install-direct">
            <Button className="w-full brand-bg text-white font-bold h-12 text-base" onClick={installNow} data-testid="install-native-button">
              <Download className="h-5 w-5 mr-2" /> {t("install.now")}
            </Button>
            <p className="text-[11px] t-secondary text-center mt-2">{t("install.direct_hint")}</p>
          </div>
        )}

        <div>
          <p className="text-sm font-semibold mb-2">{t("install.question")}</p>
          <div className="grid grid-cols-4 gap-2">
            {DEVICES.map(({ id, Icon }) => (
              <button
                key={id}
                onClick={() => setPlatform(id)}
                className={`relative flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 transition-colors ${
                  platform === id ? "border-emerald-400 bg-white/10" : "border-app hover:bg-white/5"
                }`}
                data-testid={`install-device-${id}`}
              >
                {detected === id && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-semibold uppercase tracking-wide bg-emerald-500 text-black px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid="install-detected-badge">
                    {t("install.detected")}
                  </span>
                )}
                <Icon className="h-6 w-6" />
                <span className="text-[11px] leading-tight text-center">{t(`install.device.${id}`)}</span>
                {platform === id && <Check className="h-3.5 w-3.5 text-emerald-400 absolute top-1.5 right-1.5" />}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-app bg-black/20 p-4 space-y-2" data-testid={`install-guide-${platform}`}>
          <p className="text-xs uppercase tracking-wider t-secondary">{t("install.guide_title")}</p>
          {platform === "ios" && (
            <p className="text-sm flex items-center gap-2 t-secondary">
              <ShareIcon className="h-4 w-4 shrink-0" /> {t("install.guide.ios_share")}
            </p>
          )}
          <p className="text-sm whitespace-pre-line">{t(`install.guide.${platform}`)}</p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] t-secondary">{t("install.done_hint")}</p>
          <Button variant="outline" className="border-app shrink-0" onClick={later} data-testid="install-later-button">{t("install.later")}</Button>
        </div>
      </div>
    </div>
  );
};
